import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import { DECKS } from '../data/stimuli';
import {
  createCustomDeck,
  updateCustomDeck,
  addCardToCustomDeck,
  uploadCardImage,
  uploadCoverImage,
  getCustomDeckById,
  clearCustomDeckCards,
} from '../services/firestoreService';
import { CharCounter } from '../components/forms/CharCounter';
import { sanitizeInput, CHAR_LIMITS } from '../utils/sanitize';
import { validateImage } from '../utils/validateImage';
import { logAuditEvent, AUDIT_EVENTS } from '../services/auditService';
import { LogoLockup } from '../components/LogoMark';
import {
  FiUpload, FiCamera, FiSearch,
  FiStar, FiBookOpen, FiHeart, FiFeather,
  FiZap, FiTarget, FiMusic, FiMoreHorizontal,
} from 'react-icons/fi';
import '../styles/deck-builder.css';

const ICON_OPTIONS = [
  { Icon: FiStar,      value: '⭐' },
  { Icon: FiBookOpen,  value: '📖' },
  { Icon: FiHeart,     value: '❤️' },
  { Icon: FiFeather,   value: '🌿' },
  { Icon: FiZap,       value: '💡' },
  { Icon: FiTarget,    value: '🎯' },
  { Icon: FiMusic,     value: '🎵' },
];

const PALETTE = [
  '#D6F5DC', '#F2E4D0', '#D6EAFA', '#FFF3CC', '#EDD6FA',
  '#CCF0EC', '#D0D9F5', '#E4E4E4', '#FAE4D0', '#FAD6EC',
];

const EMOJI_OPTIONS = ['🎯', '🌟', '📚', '🎨', '🏠', '🌈', '🦋', '🎵', '🌿', '💡', '🎪', '🚀']

// Pre-paired emoji + background color for the visual cover picker
const EMOJI_PRESETS = [
  { emoji: '🎯', color: '#FFF0F3' },
  { emoji: '🌟', color: '#FFFBEB' },
  { emoji: '📚', color: '#D6F5DC' },
  { emoji: '🎨', color: '#EDD6FA' },
  { emoji: '🏠', color: '#F2E4D0' },
  { emoji: '🌈', color: '#D6EAFA' },
  { emoji: '🦋', color: '#F5F3FF' },
  { emoji: '🎵', color: '#FAD6EC' },
  { emoji: '🌿', color: '#CCF0EC' },
  { emoji: '💡', color: '#FFF3CC' },
  { emoji: '🎪', color: '#FAE4D0' },
  { emoji: '🚀', color: '#D0D9F5' },
];
const STEP_LABELS   = ['Deck Info', 'Build Cards', 'Review & Save'];

const COVER_THEMES = [
  { keywords: ['yellow','gold','sun','banana','lemon','bright'], emoji: '🌟', bg: '#FFFBEB' },
  { keywords: ['animal','pet','dog','cat','bird','fish','zoo','farm','wildlife'], emoji: '🐾', bg: '#F0FDF4' },
  { keywords: ['food','eat','fruit','vegetable','snack','meal','drink','hungry'], emoji: '🍎', bg: '#FEF2F2' },
  { keywords: ['car','bus','train','truck','vehicle','transport','move','road','bike'], emoji: '🚗', bg: '#EFF6FF' },
  { keywords: ['color','colour','red','blue','green','orange','purple','pink'], emoji: '🎨', bg: '#FAF5FF' },
  { keywords: ['shape','circle','square','triangle','round','rectangle'], emoji: '🔷', bg: '#F0F9FF' },
  { keywords: ['school','learn','letter','number','read','write','book','math','count'], emoji: '📚', bg: '#FEFCE8' },
  { keywords: ['body','face','hand','foot','arm','leg','eye','nose','mouth','hair'], emoji: '🖐️', bg: '#FFF7ED' },
  { keywords: ['emotion','feeling','happy','sad','angry','scared','excited','calm'], emoji: '😊', bg: '#FFF1F2' },
  { keywords: ['home','house','room','kitchen','bedroom','bathroom','family'], emoji: '🏠', bg: '#F0FDF4' },
  { keywords: ['nature','plant','tree','flower','garden','grass','leaf','outdoor'], emoji: '🌿', bg: '#ECFDF5' },
  { keywords: ['music','sing','song','sound','instrument','dance'], emoji: '🎵', bg: '#FAF5FF' },
  { keywords: ['sport','game','ball','run','jump','swim','play','exercise'], emoji: '⚽', bg: '#F0FDF4' },
  { keywords: ['weather','rain','snow','cloud','wind','hot','cold','storm'], emoji: '⛅', bg: '#EFF6FF' },
  { keywords: ['people','community','friend','social','group','team'], emoji: '👥', bg: '#F5F3FF' },
  { keywords: ['action','verb','doing','activity'], emoji: '🎯', bg: '#FFF0F3' },
];

const DEFAULT_SUGGESTIONS = [
  { emoji: '🎯', bg: '#FFF0F3' },
  { emoji: '🌟', bg: '#FFFBEB' },
  { emoji: '📚', bg: '#FEFCE8' },
  { emoji: '🎨', bg: '#FAF5FF' },
  { emoji: '🌈', bg: '#F0F9FF' },
  { emoji: '🦋', bg: '#F5F3FF' },
];

function getEmojiSuggestions(titleText) {
  const words = titleText.toLowerCase().split(/\W+/).filter(Boolean);
  const seen = new Set();
  const scored = [];

  for (const theme of COVER_THEMES) {
    let score = 0;
    for (const word of words) {
      if (theme.keywords.some(k => word === k || k.startsWith(word) || word.startsWith(k))) score++;
    }
    if (score > 0 && !seen.has(theme.emoji)) {
      scored.push({ ...theme, score });
      seen.add(theme.emoji);
    }
  }
  scored.sort((a, b) => b.score - a.score);

  const result = [...scored];
  for (const d of DEFAULT_SUGGESTIONS) {
    if (result.length >= 6) break;
    if (!seen.has(d.emoji)) { result.push(d); seen.add(d.emoji); }
  }
  return result.slice(0, 6);
}

export const DeckBuilderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userData } = useAuth();
  const isTrial = userData?.accountType === 'trial';
  const editDeckId = location.state?.editDeckId ?? null;
  const [step, setStep] = useState(1);
  const [editLoading, setEditLoading] = useState(!!editDeckId);

  // ── Step 1 ────────────────────────────────────────────────────────────────
  const [title, setTitle]             = useState('');
  const [emoji, setEmoji]             = useState('🎯');
  const [color, setColor]             = useState(PALETTE[0]);
  const [description, setDescription] = useState('');

  // Cover (Step 1) — emoji or uploaded photo
  const [coverMode, setCoverMode]               = useState('emoji');  // 'emoji' | 'photo' | 'web'
  const [coverPhotoFile, setCoverPhotoFile]     = useState(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState(null);
  const [emojiSuggestions, setEmojiSuggestions] = useState(DEFAULT_SUGGESTIONS);

  // Emoji picker popover (Step 1)
  const [showEmojiPicker, setShowEmojiPicker]   = useState(false);
  const [showMoreIcons,   setShowMoreIcons]      = useState(false);
  const emojiPickerRef = useRef(null);

  // Cover Pexels search (Step 1)
  const [coverQuery,     setCoverQuery]     = useState('');
  const [coverResults,   setCoverResults]   = useState([]);
  const [coverSearching, setCoverSearching] = useState(false);
  const [coverSearchErr, setCoverSearchErr] = useState('');

  // ── Step 2 shared ─────────────────────────────────────────────────────────
  const [deckCards, setDeckCards]         = useState([]);
  const [recentlyDeleted, setRecentlyDeleted] = useState([]);
  const [deletedExpanded, setDeletedExpanded] = useState(false);
  const [activeTab, setActiveTab]         = useState('custom');

  // Mix tab
  const [expandedDeck, setExpandedDeck] = useState(null);

  // Custom card tab
  const [customLabel, setCustomLabel]         = useState('');
  const [customEmoji, setCustomEmoji]         = useState('');
  const [uploadedFile, setUploadedFile]       = useState(null);
  const [uploadedPreview, setUploadedPreview] = useState(null);
  const [imagePickerMode, setImagePickerMode] = useState('search'); // 'search' | 'camera' | 'photos'
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  const [imageSearchResults, setImageSearchResults] = useState([]);
  const [imageSearching, setImageSearching]   = useState(false);
  const [selectedSearchPhotos, setSelectedSearchPhotos] = useState([]); // multi-select

  // Upload validation / progress
  const [coverUploadError, setCoverUploadError] = useState('');
  const [cardUploadError, setCardUploadError]   = useState('');
  const [uploadProgress, setUploadProgress]     = useState(0); // 0-100

  // Auto-search debounce ref
  const autoSearchRef = useRef(null);
  const [varPreviewCard, setVarPreviewCard] = useState(null);
  const cardTapRef = useRef({});

  // ── Step 3 / Save state ───────────────────────────────────────────────────
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedDeckId, setSavedDeckId] = useState(null);
  // Tracks a deck doc created by a previous, partially-failed save attempt
  // (create mode only), so retrying re-uses it instead of creating a
  // duplicate deck and orphaning the incomplete one.
  const pendingDeckIdRef = useRef(null);

  // ── Post-save modal ───────────────────────────────────────────────────────
  const [showModal, setShowModal]       = useState(false);
  const [toastVisible, setToastVisible] = useState(false);

  // ── Unsaved-changes modal (replaces useBlocker which requires a data router) ──
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNav, setPendingNav]             = useState(null);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const fileInputRef         = useRef(null);
  const cameraInputRef       = useRef(null);
  const coverFileInputRef    = useRef(null);
  const coverCameraInputRef  = useRef(null);
  const toastTimerRef        = useRef(null);

  // ── Derived ───────────────────────────────────────────────────────────────
  const currentCardImage = uploadedPreview || (selectedSearchPhotos[0]?.src?.medium ?? null);
  const isDirty          = (title.trim().length > 0 || deckCards.length > 0) && !savedDeckId;

  // ── Edit mode: pre-load existing deck ─────────────────────────────────────
  useEffect(() => {
    if (!editDeckId) return;
    getCustomDeckById(editDeckId).then(result => {
      if (result.success) {
        const d = result.deck;
        setTitle(d.name || '');
        setEmoji(d.emoji || '🎯');
        if (d.color) setColor(d.color);
        if (d.coverImage) {
          setCoverMode('photo');
          setCoverPhotoPreview(d.coverImage);
        }
        const loaded = d.items.map((item, i) => ({
          id: `loaded-${i}-${Date.now()}`,
          label: item.label,
          emoji: item.emoji || '🃏',
          image: item.image || null,
          imageFile: null,
          imageType: item.image ? 'remote' : null,
          variations: item.variations || [],
        }));
        setDeckCards(loaded);
      }
      setEditLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDeckId]);

  // Browser refresh / tab-close warning
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Close emoji picker on outside click
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handler = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEmojiPicker]);

  // Clean up object URLs and intervals on unmount
  useEffect(() => {
    return () => {
      if (uploadedPreview) URL.revokeObjectURL(uploadedPreview);
      if (coverPhotoPreview) URL.revokeObjectURL(coverPhotoPreview);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (autoSearchRef.current) clearTimeout(autoSearchRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Safe navigation (in-app) ──────────────────────────────────────────────
  // Sidebar/navbar <Link> clicks bypass this — for those, beforeunload handles refresh/close
  const safeNavigate = useCallback((to, opts) => {
    if (isDirty) {
      setPendingNav({ to, opts });
      setShowUnsavedModal(true);
    } else {
      navigate(to, opts);
    }
  }, [isDirty, navigate]);

  const handleConfirmLeave = useCallback(async (action) => {
    if (action === 'save') {
      const ok = await handleSaveInternal();
      if (!ok) { setShowUnsavedModal(false); return; }
    }
    setShowUnsavedModal(false);
    const dest = pendingNav;
    setPendingNav(null);
    if (dest) navigate(dest.to, dest.opts);
  }, [pendingNav, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-search helper (fires after 600ms idle on label change) ─────────
  const triggerAutoSearch = useCallback((label) => {
    if (autoSearchRef.current) clearTimeout(autoSearchRef.current);
    if (!label.trim()) return;
    autoSearchRef.current = setTimeout(async () => {
      setImageSearchQuery(label.trim());
      setImageSearching(true);
      setSelectedSearchPhotos([]);
      try {
        const res = await fetch(
          `https://api.pexels.com/v1/search?query=${encodeURIComponent(label.trim())}&per_page=15&orientation=square`,
          { headers: { Authorization: process.env.REACT_APP_PEXELS_API_KEY } }
        );
        const data = await res.json();
        setImageSearchResults(data.photos || []);
      } catch {
        setImageSearchResults([]);
      } finally {
        setImageSearching(false);
      }
    }, 600);
  }, []);

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = () => {
    setToastVisible(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 2800);
  };

  // ── Cover helpers ─────────────────────────────────────────────────────────
  const handleTitleBlur = () => {
    const text = title.trim();
    if (!text) return;
    const suggestions = getEmojiSuggestions(text);
    setEmojiSuggestions(suggestions);
    // Auto-apply top suggestion to the deck emoji + color
    if (suggestions.length > 0) {
      setEmoji(suggestions[0].emoji);
      setColor(suggestions[0].bg);
    }
  };

  const handlePickCoverTheme = ({ emoji: em, bg }) => {
    setEmoji(em);
    setColor(bg);
  };

  const handleCoverFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setCoverUploadError('');
    const result = await validateImage(file);
    if (!result.valid) { setCoverUploadError(result.error); return; }
    if (coverPhotoPreview) URL.revokeObjectURL(coverPhotoPreview);
    setCoverPhotoFile(file);
    setCoverPhotoPreview(URL.createObjectURL(file));
    setCoverMode('photo');
  };

  const handleClearCoverPhoto = () => {
    if (coverPhotoPreview) URL.revokeObjectURL(coverPhotoPreview);
    setCoverPhotoFile(null);
    setCoverPhotoPreview(null);
    setCoverMode('emoji');
  };

  // ── Card image helpers ────────────────────────────────────────────────────
  const handleCardFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setCardUploadError('');
    const result = await validateImage(file);
    if (!result.valid) { setCardUploadError(result.error); return; }
    if (uploadedPreview) URL.revokeObjectURL(uploadedPreview);
    setUploadedFile(file);
    setUploadedPreview(URL.createObjectURL(file));
    setImagePickerMode('photos');
    setSelectedSearchPhotos([]);
  };

  const PEXELS_KEY = process.env.REACT_APP_PEXELS_API_KEY;

  const handleSearchImages = async (query) => {
    const q = (query || imageSearchQuery || customLabel).trim();
    if (!q) return;
    setImageSearching(true);
    setImageSearchResults([]);
    setSelectedSearchPhotos([]);
    try {
      const res = await fetch(
        `https://api.pexels.com/v1/search?query=${encodeURIComponent(q)}&per_page=15&orientation=square`,
        { headers: { Authorization: PEXELS_KEY } }
      );
      const data = await res.json();
      setImageSearchResults(data.photos || []);
    } catch {
      setImageSearchResults([]);
    } finally {
      setImageSearching(false);
    }
  };

  // Toggle photo in multi-select list
  const toggleSearchPhoto = (photo) => {
    setSelectedSearchPhotos(prev =>
      prev.some(p => p.id === photo.id)
        ? prev.filter(p => p.id !== photo.id)
        : [...prev, photo]
    );
  };

  // Add all selected photos: first = card image, rest = stored as variations
  const handleAddSelectedPhotos = () => {
    if (!customLabel.trim() || selectedSearchPhotos.length === 0) return;
    const label = customLabel.trim();
    const [first, ...rest] = selectedSearchPhotos;
    const cardEmoji = customEmoji.trim() || '🖼️';

    setDeckCards(prev => [...prev, {
      id: Date.now(),
      label,
      emoji: cardEmoji,
      image: first.src.medium,
      imageFile: null,
      imageType: 'web',
      variations: rest.map(p => p.src.medium),
    }]);

    setCustomLabel('');
    setCustomEmoji('');
    if (uploadedPreview) URL.revokeObjectURL(uploadedPreview);
    setUploadedFile(null);
    setUploadedPreview(null);
    setImagePickerMode('search');
    setImageSearchQuery('');
    setImageSearchResults([]);
    setSelectedSearchPhotos([]);
    if (autoSearchRef.current) clearTimeout(autoSearchRef.current);
  };

  const handlePickSearchResult = (photo) => {
    toggleSearchPhoto(photo);
    setUploadedFile(null);
    if (uploadedPreview) URL.revokeObjectURL(uploadedPreview);
    setUploadedPreview(null);
    setImagePickerMode('search');
  };

  // ── Cover photo Pexels search ─────────────────────────────────────────────
  const handleCoverSearch = async (e) => {
    e?.preventDefault();
    const q = coverQuery.trim() || title.trim();
    if (!q) return;
    setCoverSearching(true);
    setCoverSearchErr('');
    try {
      const res = await fetch(
        `https://api.pexels.com/v1/search?query=${encodeURIComponent(q)}&per_page=12&orientation=landscape`,
        { headers: { Authorization: PEXELS_KEY } }
      );
      const data = await res.json();
      if (!data.photos?.length) setCoverSearchErr('No photos found — try different words.');
      setCoverResults(data.photos || []);
    } catch {
      setCoverSearchErr('Search failed. Check your connection.');
    }
    setCoverSearching(false);
  };

  const handlePickCoverPexels = (photo) => {
    const url = photo.src?.large || photo.src?.medium;
    setCoverPhotoPreview(url);
    setCoverPhotoFile(null); // it's a URL, not a local file
    setCoverMode('photo');
    setCoverResults([]);
  };

  const handleAddCustomCard = () => {
    if (!customLabel.trim() || !currentCardImage) return;
    setDeckCards(prev => [...prev, {
      id: Date.now(),
      label: customLabel.trim(),
      emoji: customEmoji.trim() || '🖼️',
      image: currentCardImage,
      imageFile: uploadedFile,
      imageType: uploadedFile ? 'uploaded' : 'web',
      variations: [],
    }]);
    setCustomLabel('');
    setCustomEmoji('');
    if (uploadedPreview) URL.revokeObjectURL(uploadedPreview);
    setUploadedFile(null);
    setUploadedPreview(null);
    setImagePickerMode('search');
    setImageSearchQuery('');
    setImageSearchResults([]);
    setSelectedSearchPhotos([]);
    if (autoSearchRef.current) clearTimeout(autoSearchRef.current);
  };

  const handlePickExistingCard = (item) => {
    const id = `existing-${item.label}`;
    setDeckCards(prev =>
      prev.some(c => c.id === id)
        ? prev.filter(c => c.id !== id)
        : [...prev, { id, label: item.label, emoji: item.emoji, image: item.image, imageFile: null, imageType: 'generated', source: 'existing' }]
    );
  };

  // ── Recently Deleted ──────────────────────────────────────────────────────
  const removeCard = (cardId) => {
    const card = deckCards.find(c => c.id === cardId);
    if (!card) return;
    setDeckCards(prev => prev.filter(c => c.id !== cardId));
    setRecentlyDeleted(prev => [...prev, { ...card, deletedAt: Date.now() }]);
  };

  const restoreCard = (cardId) => {
    const card = recentlyDeleted.find(c => c.id === cardId);
    if (!card) return;
    setRecentlyDeleted(prev => prev.filter(c => c.id !== cardId));
    const { deletedAt, ...restored } = card; // eslint-disable-line no-unused-vars
    setDeckCards(prev => [...prev, restored]);
  };

  const permanentDeleteCard = (cardId) => {
    setRecentlyDeleted(prev => prev.filter(c => c.id !== cardId));
  };

  const restoreAll = () => {
    setDeckCards(prev => [
      ...prev,
      ...recentlyDeleted.map(({ deletedAt, ...card }) => card), // eslint-disable-line no-unused-vars
    ]);
    setRecentlyDeleted([]);
  };

  function daysSince(ts) {
    const diff = Date.now() - ts;
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSaveInternal = async () => {
    if (!currentUser || saving) return false;
    setSaving(true);
    setSaveError('');
    let success = false;
    try {
      let deckId;

      if (editDeckId) {
        // ── Edit mode: update existing deck ──────────────────────────────────
        deckId = editDeckId;
        await updateCustomDeck(deckId, {
          title: sanitizeInput(title), emoji, color, description: sanitizeInput(description),
          cardCount: deckCards.length,
        });
        // Update cover
        if (coverPhotoFile) {
          const upCover = await uploadCoverImage(currentUser.uid, deckId, coverPhotoFile, () => {});
          if (upCover.success) await updateCustomDeck(deckId, { coverImage: upCover.url });
        } else if (coverPhotoPreview && coverMode === 'photo') {
          await updateCustomDeck(deckId, { coverImage: coverPhotoPreview });
        }
        // Replace all cards
        await clearCustomDeckCards(deckId);
      } else if (pendingDeckIdRef.current) {
        // ── Retry of a previously partial-failed create-mode save ────────────
        // The deck doc already exists from the last attempt — reuse it and
        // wipe whatever cards it partially got, rather than creating a
        // second duplicate deck and leaving the broken one behind.
        deckId = pendingDeckIdRef.current;
        await clearCustomDeckCards(deckId);

        if (coverPhotoFile) {
          const upCover = await uploadCoverImage(currentUser.uid, deckId, coverPhotoFile, () => {});
          if (upCover.success) await updateCustomDeck(deckId, { coverImage: upCover.url });
        } else if (coverPhotoPreview && coverMode === 'photo') {
          await updateCustomDeck(deckId, { coverImage: coverPhotoPreview });
        }
      } else {
        // ── Create mode: new deck ─────────────────────────────────────────────
        const deckResult = await createCustomDeck(currentUser.uid, {
          title: sanitizeInput(title), emoji, color, description: sanitizeInput(description),
          cardCount: deckCards.length,
          coverImage: (!coverPhotoFile && coverMode === 'photo' && coverPhotoPreview) ? coverPhotoPreview : null,
          createdAt: new Date(),
        });
        if (!deckResult.success) throw new Error(deckResult.error);
        deckId = deckResult.id;
        pendingDeckIdRef.current = deckId;

        if (coverPhotoFile) {
          const upCover = await uploadCoverImage(currentUser.uid, deckId, coverPhotoFile, () => {});
          if (upCover.success) await updateCustomDeck(deckId, { coverImage: upCover.url });
        } else if (coverPhotoPreview && coverMode === 'photo') {
          await updateCustomDeck(deckId, { coverImage: coverPhotoPreview });
        }
      }

      const uploadedFileCount = deckCards.filter(c => c.imageFile).length;
      let uploadsDone = 0;
      const trackProgress = (pct) => {
        if (uploadedFileCount === 0) return;
        const base = (uploadsDone / uploadedFileCount) * 100;
        setUploadProgress(Math.round(base + pct / uploadedFileCount));
      };

      for (let i = 0; i < deckCards.length; i++) {
        const card = deckCards[i];
        let imageUrl = card.image;
        if (card.imageFile) {
          const up = await uploadCardImage(currentUser.uid, deckId, `card-${i}`, card.imageFile, trackProgress);
          if (up.success) imageUrl = up.url;
          uploadsDone++;
          setUploadProgress(Math.round((uploadsDone / uploadedFileCount) * 100));
        }
        await addCardToCustomDeck(deckId, {
          label: sanitizeInput(card.label), emoji: card.emoji,
          image: imageUrl, imageType: card.imageType, order: i,
          variations: card.variations || [],
        });
      }

      logAuditEvent(currentUser.uid, AUDIT_EVENTS.DECK_CREATED, { deckId, cardCount: deckCards.length });
      setSavedDeckId(deckId);
      showToast();
      setShowModal(true);
      success = true;
    } catch (err) {
      setSaveError('Save failed — please try again. Your work is still here.');
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
    return success;
  };

  const handleSave = () => handleSaveInternal();

  // ── Render ────────────────────────────────────────────────────────────────
  const DB_STEPS = [
    { num: 1, label: 'Name & Icon' },
    { num: 2, label: 'Add Cards' },
    { num: 3, label: 'Review' },
  ];

  const dbHintText = {
    1: 'Give your deck a name and choose an icon. The color helps you recognize it quickly!',
    2: 'Add cards using image search, your camera, or photos. Each card needs a label and image.',
    3: 'Review your cards and save the deck to your library!',
  };

  if (editLoading) {
    return (
      <div className="wz-page" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}>
        <p style={{ color:'var(--text-muted)', fontSize:15 }}>Loading deck…</p>
      </div>
    );
  }

  return (
    <div className="wz-page">

      {showUnsavedModal && ReactDOM.createPortal(
        <div style={{
          position:'fixed', top:0, left:0, right:0, bottom:0,
          backgroundColor:'rgba(0,0,0,0.65)',
          zIndex:99999,
          display:'flex', alignItems:'center', justifyContent:'center',
          padding:'20px'
        }}>
          <div style={{
            backgroundColor:'#fff',
            borderRadius:'16px',
            padding:'40px 36px 32px',
            width:'100%',
            maxWidth:'420px',
            textAlign:'center',
            boxShadow:'0 40px 100px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.2)',
            border:'1px solid #e5e7eb',
            color:'#111827'
          }}>
            <div style={{ width:52, height:52, borderRadius:'50%', background:'#F59E0B', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:700, margin:'0 auto 16px' }}>!</div>
            <h2 style={{ fontSize:20, fontWeight:700, color:'#111827', margin:'0 0 8px' }}>Unsaved Changes</h2>
            <p style={{ fontSize:14, color:'#6B7280', margin:'0 0 24px' }}>You have unsaved changes. What would you like to do?</p>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <button onClick={() => handleConfirmLeave('save')} disabled={saving} type="button"
                style={{ width:'100%', height:44, borderRadius:10, border:'none', background:'#8B2635', color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'inherit', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving…' : 'Save and Leave'}
              </button>
              <button onClick={() => handleConfirmLeave('discard')} disabled={saving} type="button"
                style={{ width:'100%', height:44, borderRadius:10, border:'1.5px solid #FCA5A5', background:'#FEF2F2', color:'#DC2626', fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                Discard and Leave
              </button>
              <button onClick={() => { setShowUnsavedModal(false); setPendingNav(null); }} type="button"
                style={{ width:'100%', height:44, borderRadius:10, border:'1.5px solid #E5E7EB', background:'#F9FAFB', color:'#374151', fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                Stay and Keep Editing
              </button>
            </div>
          </div>
        </div>,
        document.getElementById('modal-root') || document.body
      )}

      {/* ── Header ── */}
      <div className="session-header-bar">
        <button className="sc-back-link" onClick={() => safeNavigate(-1)} type="button">← Back</button>
        <div className="sc-logo-crop"><LogoLockup size={24} /></div>
        <div style={{ width: 60 }} />
      </div>

      {/* ── Toast ── */}
      <div className={`db-toast${toastVisible ? ' visible' : ''}`}>✓ Deck saved to your library</div>

      <div className="wz-layout">

        {/* ── Stepper ── */}
        <div className="wz-stepper">
          {DB_STEPS.map(({ num, label }) => (
            <div key={num} className="wz-step-item">
              <div className="wz-step-track">
                <div className={`wz-step-circle ${step === num ? 'wz-active' : step > num ? 'wz-done' : 'wz-pending'}`}>
                  {step > num ? '✓' : num}
                </div>
                {num < 3 && <div className={`wz-step-line ${step > num ? 'wz-line-done' : ''}`} />}
              </div>
              <span className={`wz-step-label ${step === num ? 'wz-label-active' : ''}`}>{label}</span>
            </div>
          ))}
        </div>

        {/* ── Main card ── */}
        <div className="wz-main">
          <div className="wz-card">

            {/* ══ STEP 1: Name & Icon ══ */}
            {step === 1 && (
              <>
                <h2 className="wz-step-title">{editDeckId ? 'Edit Deck — Details' : 'Step 1: Name & Icon'}</h2>

                <div className="wz-field">
                  <label className="wz-field-label">Deck Name *</label>
                  <input
                    className="db-input db-input-large"
                    placeholder="e.g., Things That Move"
                    value={title}
                    onChange={e => { const v = e.target.value; if (v.length <= CHAR_LIMITS.deckTitle) setTitle(v); }}
                    onBlur={handleTitleBlur}
                    maxLength={CHAR_LIMITS.deckTitle}
                    autoFocus
                  />
                  <CharCounter current={title.length} max={CHAR_LIMITS.deckTitle} />
                </div>

                {/* ── Deck Cover ── */}
                <div className="wz-field">
                  <div className="db-cover-page">

                    {/* Left — current cover */}
                    <div className="db-cover-left">
                      <p className="db-cover-col-title">Current Cover</p>
                      <div className="db-cover-current-wrap">
                        {coverPhotoPreview && coverMode !== 'emoji' ? (
                          <img src={coverPhotoPreview} alt={title} className="db-cover-current-photo" />
                        ) : (
                          <div className="db-cover-current-emoji" style={{ backgroundColor: color }}>
                            <span>{emoji || '📚'}</span>
                          </div>
                        )}
                      </div>
                      {coverPhotoPreview && coverMode !== 'emoji' && (
                        <button className="db-cover-remove-btn" onClick={handleClearCoverPhoto} type="button">
                          ✎ Remove
                        </button>
                      )}
                    </div>

                    {/* Right — options */}
                    <div className="db-cover-right">
                      <p className="db-cover-col-title">Choose Your Cover</p>

                      {/* ① Background Color & Icon */}
                      <div className="db-cover-section-card">
                        <div className="db-cover-section-num">1</div>
                        <div className="db-cover-section-content">
                          <p className="db-cover-section-heading">Background Color &amp; Icon</p>
                          <p className="db-cover-section-sub">Pick a background color and add an icon (optional).</p>

                          {/* Color swatches */}
                          <div className="db-cover-color-swatches">
                            {PALETTE.map(c => (
                              <button
                                key={c}
                                type="button"
                                className={`db-cover-color-sq${color === c ? ' selected' : ''}`}
                                style={{ backgroundColor: c }}
                                onClick={() => { setColor(c); setCoverMode('emoji'); }}
                              >
                                {color === c && <span className="db-cover-color-check">✓</span>}
                              </button>
                            ))}
                          </div>

                          {/* Icon row */}
                          <div className="db-cover-icon-row">
                            {/* No icon */}
                            <button
                              type="button"
                              className={`db-cover-icon-btn${emoji === '' ? ' selected' : ''}`}
                              onClick={() => { setEmoji(''); setCoverMode('emoji'); }}
                            >
                              <span className="db-cover-icon-svg" style={{ color: '#9CA3AF', fontSize: 18 }}>⊘</span>
                              <span className="db-cover-icon-label">No icon</span>
                            </button>
                            {ICON_OPTIONS.map(({ Icon, value }) => (
                              <button
                                key={value}
                                type="button"
                                className={`db-cover-icon-btn${emoji === value ? ' selected' : ''}`}
                                onClick={() => { setEmoji(value); setCoverMode('emoji'); handleClearCoverPhoto(); }}
                              >
                                <Icon size={18} className="db-cover-icon-svg" />
                              </button>
                            ))}
                            <button
                              type="button"
                              className={`db-cover-icon-btn${showMoreIcons ? ' selected' : ''}`}
                              onClick={() => setShowMoreIcons(v => !v)}
                            >
                              <FiMoreHorizontal size={18} className="db-cover-icon-svg" />
                              <span className="db-cover-icon-label">More</span>
                            </button>
                          </div>

                          {/* Expanded emoji grid */}
                          {showMoreIcons && (
                            <div className="db-cover-more-grid">
                              {EMOJI_PRESETS.map(p => (
                                <button
                                  key={p.emoji}
                                  type="button"
                                  className={`db-cover-preset-btn${emoji === p.emoji ? ' selected' : ''}`}
                                  style={{ backgroundColor: color }}
                                  onClick={() => { setEmoji(p.emoji); setCoverMode('emoji'); handleClearCoverPhoto(); setShowMoreIcons(false); }}
                                >{p.emoji}</button>
                              ))}
                              <input
                                className="db-cover-preset-custom"
                                value={EMOJI_PRESETS.some(p => p.emoji === emoji) ? '' : emoji}
                                onChange={e => { setEmoji(e.target.value); setCoverMode('emoji'); }}
                                maxLength={2}
                                placeholder="+"
                                title="Type any emoji"
                              />
                            </div>
                          )}

                          {/* Title suggestions */}
                          {title.trim() && emojiSuggestions.length > 0 && (
                            <div className="db-cover-suggestions" style={{ marginTop: 10 }}>
                              <span className="db-cover-suggestions-label">Suggested for "{title.trim()}"</span>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 5 }}>
                                {emojiSuggestions.slice(0, 5).map((s, i) => (
                                  <button key={i} type="button"
                                    className={`db-cover-sug-btn${emoji === s.emoji && color === s.bg ? ' active' : ''}`}
                                    style={{ backgroundColor: s.bg }}
                                    onClick={() => { handlePickCoverTheme(s); setCoverMode('emoji'); handleClearCoverPhoto(); }}
                                  >{s.emoji}</button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* ② Or Use a Photo */}
                      <div className="db-cover-section-card">
                        <div className="db-cover-section-num">2</div>
                        <div className="db-cover-section-content">
                          <p className="db-cover-section-heading">Or Use a Photo</p>
                          <p className="db-cover-section-sub">Upload your own image or find one online.</p>

                          <div className="db-cover-photo-cards">
                            <button type="button" className="db-cover-photo-card" onClick={() => coverFileInputRef.current?.click()}>
                              <FiUpload size={22} className="db-cover-photo-card-icon" />
                              <span className="db-cover-photo-card-label">Upload</span>
                              <span className="db-cover-photo-card-sub">From your device</span>
                            </button>
                            <button type="button" className="db-cover-photo-card" onClick={() => coverCameraInputRef.current?.click()}>
                              <FiCamera size={22} className="db-cover-photo-card-icon" />
                              <span className="db-cover-photo-card-label">Camera</span>
                              <span className="db-cover-photo-card-sub">Take a new photo</span>
                            </button>
                            <button type="button" className={`db-cover-photo-card${coverMode === 'web' ? ' active' : ''}`} onClick={() => { setCoverMode('web'); setCoverResults([]); }}>
                              <FiSearch size={22} className="db-cover-photo-card-icon" />
                              <span className="db-cover-photo-card-label">Search Web</span>
                              <span className="db-cover-photo-card-sub">Find an image online</span>
                            </button>
                          </div>

                          {coverMode === 'web' && (
                            <div style={{ marginTop: 12 }}>
                              <form className="db-search-input-row" onSubmit={handleCoverSearch}>
                                <input className="wz-search" type="text"
                                  placeholder={title ? `Search "${title}"…` : 'e.g. "animals", "school"…'}
                                  value={coverQuery} onChange={e => setCoverQuery(e.target.value)} autoFocus
                                />
                                <button className="db-search-go-btn" type="submit" disabled={coverSearching}>
                                  {coverSearching ? '…' : 'Search'}
                                </button>
                              </form>
                              {coverSearchErr && <p className="db-upload-error" style={{ marginTop: 6 }}>{coverSearchErr}</p>}
                              {!coverSearching && !coverSearchErr && coverResults.length === 0 && (
                                <p className="wz-field-hint" style={{ marginTop: 6 }}>Real photos from Pexels — no AI images.</p>
                              )}
                              {coverResults.length > 0 && (
                                <div className="db-search-results-grid" style={{ marginTop: 10 }}>
                                  {coverResults.map(photo => (
                                    <div key={photo.id} className="db-search-result-tile" onClick={() => handlePickCoverPexels(photo)}>
                                      <img src={photo.src.small} alt={photo.alt || title} className="db-search-result-img" />
                                      <div className="db-search-result-plus">+</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* ③ Preview */}
                      <div className="db-cover-section-card">
                        <div className="db-cover-section-num">3</div>
                        <div className="db-cover-section-content db-cover-preview-row">
                          <div>
                            <p className="db-cover-section-heading">Preview</p>
                            <p className="db-cover-section-sub">See how your cover will look.</p>
                          </div>
                          <div className="db-cover-preview-mini-wrap">
                            <div className="db-cover-preview-mini" style={{ backgroundColor: color }}>
                              {coverPhotoPreview && coverMode !== 'emoji' ? (
                                <img src={coverPhotoPreview} alt={title} />
                              ) : (
                                <span>{emoji || '📚'}</span>
                              )}
                            </div>
                            <p className="db-cover-preview-mini-caption">This is how your deck<br/>will appear in your library.</p>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>

                  {coverUploadError && <p className="db-upload-error" style={{ marginTop: 8 }}>{coverUploadError}</p>}
                  <input ref={coverFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoverFileChange} />
                  <input ref={coverCameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleCoverFileChange} />
                </div>

                <div className="wz-field">
                  <label className="wz-field-label">Description <span className="db-optional">(optional)</span></label>
                  <textarea className="db-textarea" placeholder="Add a description for your deck…" value={description}
                    onChange={e => { const v = e.target.value; if (v.length <= CHAR_LIMITS.description) setDescription(v); }}
                    maxLength={CHAR_LIMITS.description} rows={3}
                  />
                  <CharCounter current={description.length} max={CHAR_LIMITS.description} />
                </div>
              </>
            )}

            {/* ══ STEP 2: Add Cards ══ */}
            {step === 2 && (
              <>
                <h2 className="wz-step-title">{editDeckId ? 'Edit Deck — Cards' : 'Step 2: Add Cards'}</h2>

                <div className="db-tabs">
                  <button className={`db-tab${activeTab === 'custom' ? ' active' : ''}`} onClick={() => setActiveTab('custom')} type="button">
                    Create Custom Cards
                  </button>
                  <button className={`db-tab${activeTab === 'mix' ? ' active' : ''}`} onClick={() => setActiveTab('mix')} type="button">
                    Mix from Existing Decks
                  </button>
                </div>

                {activeTab === 'custom' && (
                  <div className="db-custom-panel" style={{ marginTop: 16 }}>

                    {/* Sticky bar — shown when upload ready OR search photos selected */}
                    {customLabel.trim() && (uploadedPreview || selectedSearchPhotos.length > 0) && (
                      <div className="db-sticky-add-bar">
                        <div className="db-sticky-add-preview">
                          <img src={uploadedPreview || selectedSearchPhotos[0]?.src?.small} alt={customLabel} className="db-sticky-add-img" onError={e => { e.currentTarget.style.display='none'; }} />
                          <span className="db-sticky-add-label">
                            "{customLabel}"{selectedSearchPhotos.length > 1 ? ` +${selectedSearchPhotos.length - 1} variation${selectedSearchPhotos.length > 2 ? 's' : ''}` : ''}
                          </span>
                        </div>
                        <button className="db-sticky-add-btn" onClick={selectedSearchPhotos.length > 0 ? handleAddSelectedPhotos : handleAddCustomCard} type="button">
                          + Add to Deck
                        </button>
                      </div>
                    )}
                    <div className="wz-field">
                      <label className="wz-field-label">Card Label *</label>
                      <input
                        className="db-input"
                        placeholder="e.g., cat, school bus, apple…"
                        value={customLabel}
                        onChange={e => {
                          const v = e.target.value;
                          if (v.length <= CHAR_LIMITS.cardLabel) {
                            setCustomLabel(v);
                            setUploadedFile(null);
                            setUploadedPreview(null);
                            setSelectedSearchPhotos([]);
                            setImagePickerMode('search');
                            triggerAutoSearch(v);
                          }
                        }}
                        maxLength={CHAR_LIMITS.cardLabel}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); if (customLabel.trim()) handleSearchImages(customLabel.trim()); }
                        }}
                      />
                      <CharCounter current={customLabel.length} max={CHAR_LIMITS.cardLabel} />
                      {customLabel.trim() && imagePickerMode === 'search' && (
                        <p className="wz-field-hint" style={{ marginTop: 4 }}>
                          {imageSearching ? 'Searching Pexels…' : imageSearchResults.length > 0 ? 'Tap to select · first photo = card image, extras = variations' : 'Type to auto-search, or use Camera / Upload below'}
                        </p>
                      )}
                    </div>

                    {/* Photo source row — always visible */}
                    <div className="wz-field" style={{ marginTop: -4 }}>
                      <div className="db-img-source-row">
                        <button
                          className={`db-img-source-btn${imagePickerMode === 'search' ? ' active' : ''}`}
                          type="button"
                          onClick={() => { setImagePickerMode('search'); if (customLabel.trim() && imageSearchResults.length === 0) handleSearchImages(customLabel.trim()); }}
                        >
                          🔍 Search Web
                        </button>
                        <button
                          className={`db-img-source-btn${imagePickerMode === 'photos' ? ' active' : ''}`}
                          type="button"
                          onClick={() => { setImagePickerMode('photos'); fileInputRef.current?.click(); }}
                        >
                          📁 Upload
                        </button>
                        <button
                          className="db-img-source-btn"
                          type="button"
                          onClick={() => { setImagePickerMode('photos'); cameraInputRef.current?.click(); }}
                        >
                          📷 Camera
                        </button>
                      </div>

                      {/* Search results — shown when search mode */}
                      {imagePickerMode === 'search' && (
                        <div className="db-search-panel">
                          <form className="db-search-input-row" onSubmit={e => { e.preventDefault(); handleSearchImages(); }}>
                            <input
                              className="wz-search"
                              type="text"
                              placeholder={customLabel.trim() ? `Search "${customLabel.trim()}"…` : 'Type a label above to search'}
                              value={imageSearchQuery}
                              onChange={e => setImageSearchQuery(e.target.value)}
                            />
                            <button className="db-search-go-btn" type="submit" disabled={!imageSearchQuery.trim() || imageSearching}>
                              {imageSearching ? '…' : 'Search'}
                            </button>
                          </form>

                          {imageSearchResults.length > 0 && (
                            <>
                              <div className="db-search-results-grid" style={{ marginTop: 8 }}>
                                {imageSearchResults.map((photo) => {
                                  const isSelected = selectedSearchPhotos.some(p => p.id === photo.id);
                                  const selIdx = selectedSearchPhotos.findIndex(p => p.id === photo.id);
                                  return (
                                    <div
                                      key={photo.id}
                                      className={`db-search-result-tile${isSelected ? ' selected' : ''}`}
                                      onClick={() => toggleSearchPhoto(photo)}
                                    >
                                      <img src={photo.src?.small} alt={photo.alt || customLabel} className="db-search-result-img"
                                        onError={e => { e.currentTarget.parentElement.style.display = 'none'; }}
                                      />
                                      {isSelected && (
                                        <div className="db-search-result-check">
                                          {selIdx === 0 ? '★' : selIdx + 1}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              {selectedSearchPhotos.length > 0 && customLabel.trim() && (
                                <button className="db-search-add-selected-btn" type="button" onClick={handleAddSelectedPhotos}>
                                  + Add {selectedSearchPhotos.length} photo{selectedSearchPhotos.length > 1 ? 's' : ''} as "{customLabel.trim()}"
                                  {selectedSearchPhotos.length > 1 && <span className="db-search-add-sub"> · {selectedSearchPhotos.length - 1} variation{selectedSearchPhotos.length > 2 ? 's' : ''}</span>}
                                </button>
                              )}
                            </>
                          )}

                          {!imageSearching && imageSearchResults.length === 0 && imageSearchQuery && (
                            <p className="wz-field-hint" style={{ marginTop: 8 }}>No results — try a different keyword.</p>
                          )}
                        </div>
                      )}

                      {/* Uploaded photo preview */}
                      {imagePickerMode === 'photos' && uploadedPreview && (
                        <div className="db-wz-card-preview">
                          <div className="db-preview-card">
                            <img src={uploadedPreview} alt={customLabel} className="db-preview-img" />
                            <div className="db-preview-label">{customLabel || 'Card Label'}</div>
                          </div>
                          <button className="db-img-action-btn--ghost" style={{ marginTop:6, border:'1px solid var(--border)', borderRadius:6, padding:'4px 12px', fontSize:12, background:'none', cursor:'pointer', color:'var(--error)' }}
                            type="button" onClick={() => { URL.revokeObjectURL(uploadedPreview); setUploadedFile(null); setUploadedPreview(null); }}>
                            ✕ Remove
                          </button>
                        </div>
                      )}
                    </div>

                    {cardUploadError && <p className="db-upload-error">{cardUploadError}</p>}
                    <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCardFileChange} />
                    <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleCardFileChange} />

                    {/* Inline add button — shown for uploaded photos only (search uses the multi-select button) */}
                    {imagePickerMode === 'photos' && uploadedPreview && customLabel.trim() && (
                      <button
                        className="db-add-card-btn db-add-card-btn--ready"
                        onClick={handleAddCustomCard}
                        type="button"
                      >
                        + Add Card to Deck
                      </button>
                    )}
                  </div>
                )}

                {activeTab === 'mix' && (
                  <div className="db-mix-panel" style={{ marginTop: 16 }}>
                    <p className="db-panel-hint">Tap a deck to expand it, then tap cards to add them.</p>
                    <div className="db-existing-deck-list">
                      {DECKS.map(d => {
                        const isExpanded = expandedDeck === d.name;
                        return (
                          <div key={d.name} className="db-existing-deck-entry">
                            <div className={`db-existing-deck-header${isExpanded ? ' expanded' : ''}`} onClick={() => setExpandedDeck(isExpanded ? null : d.name)}>
                              <div className="db-existing-deck-color" style={{ backgroundColor: d.color }}><span>{d.emoji}</span></div>
                              <span className="db-existing-deck-name">{d.name}</span>
                              <span className="db-existing-deck-item-count">{d.items.length} cards</span>
                              <span className="db-existing-deck-chevron">{isExpanded ? '▲' : '▼'}</span>
                            </div>
                            {isExpanded && (
                              <div className="db-deck-items-grid">
                                {d.items.map(item => {
                                  const isPicked = deckCards.some(c => c.id === `existing-${item.label}`);
                                  return (
                                    <div key={item.label} className={`db-item-tile${isPicked ? ' picked' : ''}`} onClick={() => handlePickExistingCard(item)}>
                                      <div className="db-item-face" style={{ backgroundColor: d.color }}>
                                        <span className="db-item-emoji">{item.emoji}</span>
                                        {item.image && <img src={item.image} alt={item.label} className="db-item-img" onError={e => { e.currentTarget.style.display = 'none'; }} />}
                                        {isPicked && <div className="db-item-check">✓</div>}
                                      </div>
                                      <span className="db-item-label">{item.label}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Cards added so far */}
                {deckCards.length > 0 && (
                  <div className="db-wz-added-cards">
                    <div className="db-wz-added-header">
                      <span className="wz-field-label" style={{ margin: 0 }}>Cards in this deck</span>
                      <span className="wz-count-badge">{deckCards.length}</span>
                    </div>
                    <div className="db-preview-grid">
                      {deckCards.map(card => {
                        const allPhotos = [card.image, ...(card.variations || [])].filter(Boolean);
                        const hasVariations = allPhotos.length > 1;
                        const handleThumbTap = () => {
                          if (!hasVariations) return;
                          if (cardTapRef.current[card.id]) {
                            clearTimeout(cardTapRef.current[card.id]);
                            cardTapRef.current[card.id] = null;
                            setVarPreviewCard(card);
                          } else {
                            cardTapRef.current[card.id] = setTimeout(() => {
                              cardTapRef.current[card.id] = null;
                            }, 350);
                          }
                        };
                        return (
                          <div
                            key={card.id}
                            className={`db-preview-thumb${hasVariations ? ' db-preview-thumb--has-vars' : ''}`}
                            onClick={handleThumbTap}
                            title={hasVariations ? 'Double-click to see all selected photos' : undefined}
                          >
                            <div className="db-preview-thumb-face" style={{ backgroundColor: color }}>
                              <span className="db-preview-thumb-emoji">{card.emoji}</span>
                              {card.image && <img src={card.image} alt={card.label} className="db-preview-thumb-img" onError={e => { e.currentTarget.style.display = 'none'; }} />}
                              {hasVariations && (
                                <span className="db-preview-thumb-var-badge">{allPhotos.length}</span>
                              )}
                              <button className="db-preview-thumb-remove" onClick={e => { e.stopPropagation(); removeCard(card.id); }} type="button">×</button>
                            </div>
                            <span className="db-preview-thumb-label">{card.label}</span>
                          </div>
                        );
                      })}
                    </div>
                    {deckCards.some(c => (c.variations || []).length > 0) && (
                      <p className="db-var-hint-note">Double-click a card to see all its selected photos</p>
                    )}
                  </div>
                )}

                {/* Recently Deleted */}
                <div className="db-recently-deleted" style={{ marginTop: 16 }}>
                  <button className="db-rd-toggle" onClick={() => setDeletedExpanded(e => !e)} type="button">
                    <span className="db-rd-toggle-label">Recently Deleted{recentlyDeleted.length > 0 && <span className="db-rd-badge">{recentlyDeleted.length}</span>}</span>
                    <span className="db-rd-chevron">{deletedExpanded ? '▲' : '▼'}</span>
                  </button>
                  {deletedExpanded && (
                    <div className="db-rd-body">
                      {recentlyDeleted.length === 0 ? <p className="db-rd-empty">No recently deleted items</p> : (
                        <>
                          <button className="db-rd-restore-all" onClick={restoreAll} type="button">Restore All</button>
                          <div className="db-rd-grid">
                            {recentlyDeleted.map(card => (
                              <div key={card.id} className="db-rd-card">
                                <div className="db-rd-card-face" style={{ backgroundColor: color }}>
                                  <span className="db-rd-card-emoji">{card.emoji}</span>
                                  {card.image && <img src={card.image} alt={card.label} className="db-rd-card-img" onError={e => { e.currentTarget.style.display = 'none'; }} />}
                                </div>
                                <span className="db-rd-card-label">{card.label}</span>
                                <span className="db-rd-deleted-at">Deleted {daysSince(card.deletedAt)}</span>
                                <div className="db-rd-actions">
                                  <button className="db-rd-restore-btn" onClick={() => restoreCard(card.id)} type="button">Restore</button>
                                  <button className="db-rd-perma-btn" onClick={() => permanentDeleteCard(card.id)} type="button">Delete Forever</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ══ STEP 3: Review / Saved ══ */}
            {step === 3 && !showModal && (
              <>
                <h2 className="wz-step-title">Step 3: Review &amp; Save</h2>

                <div className="db-review-header">
                  <div className="db-review-deck-face" style={{ backgroundColor: color }}>
                    {coverMode === 'photo' && coverPhotoPreview
                      ? <img src={coverPhotoPreview} alt={title} className="db-review-cover-img" />
                      : <span className="db-review-deck-emoji">{emoji}</span>
                    }
                  </div>
                  <div>
                    <h3 className="db-review-deck-title">{title}</h3>
                    <p className="db-review-deck-count">{deckCards.length} card{deckCards.length !== 1 ? 's' : ''}</p>
                    <button className="db-review-change-cover" onClick={() => setStep(1)} type="button">Edit Cover</button>
                  </div>
                </div>

                <div className="db-review-grid">
                  {deckCards.map(card => (
                    <div key={card.id} className="db-review-card">
                      <div className="db-review-card-face" style={{ backgroundColor: color }}>
                        <span className="db-review-card-emoji">{card.emoji}</span>
                        {card.image && <img src={card.image} alt={card.label} className="db-review-card-img" onError={e => { e.currentTarget.style.display = 'none'; }} />}
                      </div>
                      <span className="db-review-card-label">{card.label}</span>
                    </div>
                  ))}
                </div>

                {saveError && <p className="db-save-error">{saveError}</p>}

                {saving && uploadProgress > 0 && (
                  <div className="db-upload-progress">
                    <div className="db-upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
                    <span className="db-upload-progress-label">{uploadProgress}%</span>
                  </div>
                )}

              </>
            )}

            {/* ══ SAVED CONFIRMATION (replaces step 3 content) ══ */}
            {showModal && (
              <div className="db-saved-confirm">
                <div className="db-saved-check">✓</div>
                <h2 className="db-saved-title">Deck Saved!</h2>
                <p className="db-saved-subtitle">"{title}" has been added to your library</p>
                <div className="db-saved-actions">
                  <button
                    className="db-saved-primary"
                    onClick={() => navigate(`/session/${encodeURIComponent(title)}?deckId=${savedDeckId}`)}
                    type="button"
                  >
                    Use This Deck Now
                  </button>
                  <button
                    className="db-saved-secondary"
                    onClick={() => navigate('/library', { state: { newDeckId: savedDeckId } })}
                    type="button"
                  >
                    Go to My Library
                  </button>
                  <button
                    className="db-saved-text-btn"
                    onClick={() => navigate('/create-deck')}
                    type="button"
                  >
                    Create Another Deck
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── Fixed footer: hint + nav (hidden after save) ── */}
      <div className={`wz-footer${showModal ? ' wz-footer--hidden' : ''}`}>
        <div className="wz-footer-hint">
          <span className="wz-hint-text">{dbHintText[step]}</span>
        </div>
        <div className="wz-footer-nav">
          {step > 1 && (
            <button className="wz-back-btn" type="button" onClick={() => setStep(s => s - 1)} disabled={saving}>← Back</button>
          )}
          {step < 3 ? (
            <button className="wz-next-btn" type="button"
              disabled={step === 1 ? !title.trim() : deckCards.length === 0}
              onClick={() => setStep(s => s + 1)}
            >
              Next →
            </button>
          ) : (
            <button className="wz-next-btn" type="button"
              onClick={handleSave}
              disabled={saving || !!savedDeckId}
            >
              {saving ? 'Saving…' : savedDeckId ? '✓ Saved' : '✓ Save Deck'}
            </button>
          )}
        </div>
      </div>

      {/* ── Card variation preview (double-click in Step 2) ── */}
      {varPreviewCard && ReactDOM.createPortal(
        (() => {
          const seen = new Set();
          const allImgs = [varPreviewCard.image, ...(varPreviewCard.variations || [])]
            .filter(src => { if (!src || seen.has(src)) return false; seen.add(src); return true; })
            .map((src, i) => ({ src, label: `Photo ${i + 1}` }));
          return (
            <div
              style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.7)', zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
              onClick={() => setVarPreviewCard(null)}
            >
              <div
                style={{ backgroundColor:'#ffffff', borderRadius:'18px', width:'100%', maxWidth:'480px', maxHeight:'82vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 40px 100px rgba(0,0,0,0.45)', border:'1px solid #E5E7EB' }}
                onClick={e => e.stopPropagation()}
              >
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 20px 14px', borderBottom:'1px solid #F3F4F6' }}>
                  <span style={{ fontSize:15, fontWeight:700, color:'#111827' }}>{varPreviewCard.label} — All Photos ({allImgs.length})</span>
                  <button onClick={() => setVarPreviewCard(null)} style={{ width:28, height:28, borderRadius:'50%', border:'1px solid #E5E7EB', background:'#F9FAFB', fontSize:16, color:'#6B7280', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'inherit', lineHeight:1 }}>×</button>
                </div>
                <div style={{ overflowY:'auto', padding:'16px', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(130px, 1fr))', gap:'12px' }}>
                  {allImgs.map((img, i) => (
                    <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
                      <img src={img.src} alt={img.label} style={{ width:'100%', aspectRatio:'1', objectFit:'cover', borderRadius:10, border:'1px solid #E5E7EB' }}
                        onError={e => { e.currentTarget.style.opacity = '0.2'; }}
                      />
                      <span style={{ fontSize:11, fontWeight:600, color:'#6B7280' }}>{img.label}</span>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize:12, color:'#9CA3AF', textAlign:'center', padding:'12px 20px 16px', borderTop:'1px solid #F3F4F6', lineHeight:1.5 }}>
                  These are the photos that will rotate during sessions for "{varPreviewCard.label}"
                </p>
              </div>
            </div>
          );
        })(),
        document.getElementById('modal-root') || document.body
      )}

    </div>
  );
};
